import { useState } from 'react';
import { ShoppingCart, Plus, Minus, Trash2, ChevronDown } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from './ui/sheet';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { TrustBadges } from './TrustBadges';
import { useNavigate } from 'react-router-dom';

export const FloatingCart = () => {
  const { cart, getCartCount, getCartTotal, getCartBaseTotal, getCartTaxable, updateQuantity, removeFromCart, isCartOpen, setIsCartOpen } = useCart();
  const navigate = useNavigate();
  const count = getCartCount();
  const [showDetails, setShowDetails] = useState(false);

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <>
      {count > 0 && (
        <button
          onClick={() => setIsCartOpen(true)}
          className="floating-cart-btn fixed bottom-6 left-6 z-[9999] h-14 w-14 rounded-full bg-[#111214] text-white shadow-xl hover:bg-[#E02128] transition-all duration-200 flex items-center justify-center hover:scale-105 border border-[#111214]"
          data-testid="floating-cart-button"
          style={{ pointerEvents: 'auto' }}
        >
          <ShoppingCart className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-[#E02128] text-white text-xs font-bold flex items-center justify-center">
            {count}
          </span>
        </button>
      )}

      <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md bg-white text-[#111214] border-l border-[#E4E4E7] flex flex-col"
          data-testid="cart-sheet"
        >
          <SheetHeader>
            <SheetTitle className="text-[#111214] font-heading font-semibold text-xl">Your Cart ({count} items)</SheetTitle>
            <SheetDescription className="sr-only">Review the paddles in your cart, adjust quantities, and proceed to checkout.</SheetDescription>
          </SheetHeader>

          {cart && cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[60vh] text-[#52525B]">
              <ShoppingCart className="w-16 h-16 mb-4 text-[#A1A1AA]" />
              <p className="text-lg">Your cart is empty</p>
              <p className="text-sm mt-2">Add some paddles to get started!</p>
            </div>
          ) : (
            <>
              <div className="mt-6 space-y-4 overflow-y-auto max-h-[60vh]">
                {cart.map((item) => (
                  <div
                    key={item.productId}
                    className="flex gap-4 p-4 rounded-lg bg-[#F9F9FA] border border-[#E4E4E7]"
                    data-testid="cart-line-item"
                  >
                    {item.image.endsWith('.mp4') ? (
                      <video
                        src={item.image}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-20 h-20 object-contain rounded bg-[#F4F4F5]"
                      />
                    ) : (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-20 h-20 object-contain rounded bg-[#F4F4F5]"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm text-[#111214]">{item.name}</h4>
                      <p className="text-[#52525B] text-xs font-mono mt-1">
                        ₹{item.priceINR.toLocaleString('en-IN')}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => updateQuantity(item.productId, item.qty - 1)}
                          className="h-7 w-7 rounded border border-[#E4E4E7] text-[#111214] hover:bg-[#F4F4F5] flex items-center justify-center transition-colors"
                          data-testid="cart-quantity-decrement"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center font-mono text-sm">{item.qty}</span>
                        <button
                          onClick={() => updateQuantity(item.productId, item.qty + 1)}
                          className="h-7 w-7 rounded border border-[#E4E4E7] text-[#111214] hover:bg-[#F4F4F5] flex items-center justify-center transition-colors"
                          data-testid="cart-quantity-increment"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.productId)}
                          className="ml-auto h-7 w-7 rounded border border-[#E4E4E7] hover:bg-[#E02128] hover:text-white hover:border-[#E02128] text-[#52525B] flex items-center justify-center transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Separator className="my-6 bg-[#E4E4E7]" />

              <div className="space-y-2">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total (MRP)</span>
                  <span className="font-mono" data-testid="order-summary-total">
                    ₹{getCartTotal().toLocaleString('en-IN')}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#52525B] underline underline-offset-4 decoration-[#E4E4E7] hover:text-[#111214] hover:decoration-[#E02128] transition-colors"
                  data-testid="cart-show-details-btn"
                  aria-expanded={showDetails}
                >
                  {showDetails ? 'Hide details' : 'Show details'}
                  <ChevronDown className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
                </button>
                {showDetails && (() => {
                  const total = getCartTotal();
                  const baseTotal = getCartBaseTotal();
                  const taxable = getCartTaxable();
                  const prodDiscount = baseTotal - taxable;
                  const gst = total - taxable;
                  return (
                    <div className="rounded-lg border border-[#E4E4E7] bg-[#F9F9FA] p-3 space-y-1.5 text-sm" data-testid="cart-price-details">
                      <div className="flex justify-between">
                        <span className="text-[#52525B]">Base price (excl. GST)</span>
                        <span className="font-mono" data-testid="cart-base-price">₹{baseTotal.toLocaleString('en-IN')}</span>
                      </div>
                      {prodDiscount > 0 && (
                        <>
                          <div className="flex justify-between text-[#E02128]">
                            <span>Discount</span>
                            <span className="font-mono" data-testid="cart-product-discount">− ₹{prodDiscount.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#52525B]">Taxable value</span>
                            <span className="font-mono" data-testid="cart-taxable">₹{taxable.toLocaleString('en-IN')}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between">
                        <span className="text-[#52525B]">GST (18%)</span>
                        <span className="font-mono" data-testid="cart-gst">+ ₹{gst.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between pt-1.5 mt-0.5 border-t border-[#E4E4E7] font-semibold">
                        <span>Total (incl. GST)</span>
                        <span className="font-mono">₹{total.toLocaleString('en-IN')}</span>
                      </div>
                      <p className="text-[11px] text-[#A1A1AA]">Inclusive of all taxes · GST @ 18% · Coupons can be applied at checkout.</p>
                    </div>
                  );
                })()}
              </div>

              <SheetFooter className="mt-6">
                <div className="w-full space-y-3">
                  <Button
                    onClick={handleCheckout}
                    className="w-full h-12 bg-[#E02128] hover:bg-[#111214] text-white font-bold uppercase tracking-wider transition-colors duration-300"
                    data-testid="checkout-cta"
                  >
                    Proceed to Checkout
                  </Button>
                  <TrustBadges compact />
                </div>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
